import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStaticQuery, graphql } from 'gatsby';
import BackgroundImage from 'gatsby-background-image';

import { GatsbyImageSharpFluid_withWebp } from '../types';
import {
  useOnLoadEffect,
  usePagination,
  useUpdateUrlFromFilters,
  useWindowSize,
} from '../utils/hooks';
import Video from './Video';
import clsx from 'clsx';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { areSameFlatColor, flatten, mapBy, sortBy, uniq } from '../utils/utils';
import { TextField } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { darkTheme, lightTheme } from '../styles/themes';

import { Crop, Product } from '../pages/products';
import { CommonSectionProps, SectionBgColor } from '../templates/Page';

import * as styles from './VideoSection.module.scss';
import * as filterStyles from '../components/FilterBox.module.scss';

export const VideosSectionFragment = graphql`
  fragment VideosSection on SanityVideosSection {
    background
    title
    videos {
      title
      url
      date
      products {
        _id
        name
      }
      crops {
        _id
        name
      }
    }
    withFilters
  }
`;

type ID = string;

interface VideoProps {
  id: ID;
  title: string;
  url: string;
  date?: string;
  products?: Array<{
    _id: string;
  }>;
  crops?: Array<{
    _id: string;
  }>;
}

export interface VideosSectionProps {
  background: 'white' | 'darkBlueImage';
  withFilters?: boolean;
  title: string;
  videos: Array<VideoProps>;
  className?: string;
  videosPerPage?: number;
}

interface QueryData {
  backgroundImage: {
    childImageSharp: {
      fluid: GatsbyImageSharpFluid_withWebp;
    };
  };
  allSanityProduct: {
    nodes: Array<Product>;
  };
  allSanityCrop: {
    nodes: Array<Crop>;
  };
}

type Filters = Record<'product' | 'crop' | string, ID | null>;

export function getSectionBgColor(props: VideosSectionProps): SectionBgColor<'white'> {
  return {
    name: 'white',
    type: props.background === 'white' ? 'solid' : 'textured',
  };
}

function VideosSection<PrevSectionColorName extends string>(
  props: VideosSectionProps & CommonSectionProps<PrevSectionColorName>,
): React.ReactElement {
  const {
    background,
    withFilters,
    title,
    videos,
    videosPerPage,
    className,
    previousSectionBgColor,
  } = props;

  const data: QueryData = useStaticQuery(
    graphql`
      {
        backgroundImage: file(relativePath: { eq: "videos-section-bg.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        allSanityProduct {
          nodes {
            _id
            name
            slug {
              current
            }
          }
        }
        allSanityCrop {
          nodes {
            _id
            name
            slug {
              current
            }
            group {
              _id
              name
            }
          }
        }
      }
    `,
  );

  const { width } = useWindowSize();
  const { t } = useTranslation();

  const {
    allSanityProduct: { nodes: allProducts },
    allSanityCrop: { nodes: allCrops },
  } = data;

  let maxVideoWidth: undefined | number = undefined;
  if (width) {
    if (width < 768) {
      maxVideoWidth = (width - 40) * 2; // *2 for retina
    } else if (width < 1200) {
      maxVideoWidth = width - 40 * 3; // *2 for retina
    } else {
      maxVideoWidth = 400;
    }
  }

  const [activeFilters, setActiveFilters] = useState<Filters>({
    product: null,
    crop: null,
  });

  const productsById = mapBy(allProducts, '_id');
  const cropsById = mapBy(allCrops, '_id');

  function updateFilters(filterName: string, value: string | null) {
    setActiveFilters({
      ...activeFilters,
      [filterName]: value,
    });
    resetPagination();
  }

  const filteredVideos = videos.filter(
    video =>
      (!activeFilters.product ||
        video.products?.map(product => product._id).includes(activeFilters.product)) &&
      (!activeFilters.crop || video.crops?.map(crop => crop._id).includes(activeFilters.crop)),
  );

  const videosWithProducts = filteredVideos.filter(
    video => video.products && video.products.length > 0,
  );
  const videosWithCrops = filteredVideos.filter(video => video.crops && video.crops.length > 0);

  // TODO see applyFiltersToIndexedProducts in products.tsx
  const productsIdsForFilter = uniq(
    flatten(videosWithProducts.map(video => video.products!.map(product => product._id))),
  );

  const cropsIdsForFilter = uniq(
    flatten(videosWithCrops.map(video => video.crops!.map(crop => crop._id))),
  );

  const {
    currentPage,
    currentPageItems: currentPageVideos,
    getPageUrlPart,
    resetPagination,
    renderPagination,
  } = usePagination(filteredVideos, videosPerPage || 10);

  // Get initial filters state from url
  const hasRunOnLoadEffect = useOnLoadEffect(() => {
    let initialProductId: string | null = null;
    let initialCropId: string | null = null;

    const productsBySlug = mapBy(allProducts, product => product.slug.current);
    const cropsBySlug = mapBy(allCrops, crop => crop.slug.current);

    const currentUrl = new URLSearchParams(window.location.search);
    const urlProductSlugEncoded = currentUrl.get('product');
    const urlCropSlugEncoded = currentUrl.get('crop');

    if (urlProductSlugEncoded) {
      const initialProduct = productsBySlug[decodeURIComponent(urlProductSlugEncoded)];
      initialProductId = initialProduct ? initialProduct._id : null;
    }
    if (urlCropSlugEncoded) {
      const initialCrop = cropsBySlug[decodeURIComponent(urlCropSlugEncoded)];
      initialCropId = initialCrop ? initialCrop._id : null;
    }
    setActiveFilters({
      product: initialProductId,
      crop: initialCropId,
    });
  });

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    const product = activeFilters.product ? productsById[activeFilters.product] : null;
    const productSlugEncoded = product ? encodeURIComponent(product.slug.current) : null;
    const crop = activeFilters.crop ? cropsById[activeFilters.crop] : null;
    const cropSlugEncoded = crop ? encodeURIComponent(crop.slug.current) : null;
    return [
      ['product', productSlugEncoded] as const,
      ['crop', cropSlugEncoded] as const,
      getPageUrlPart(),
    ];
  }, [
    ...sortBy(Object.entries(activeFilters), ([key]) => key).map(([, value]) => value),
    currentPage,
  ]);

  return (
    <BackgroundImage
      className={clsx(styles.wrapper, className)}
      fluid={data.backgroundImage.childImageSharp.fluid}
    >
      <section
        id={props.sectionID || ''}
        className={clsx(
          `dark ${styles.section}`,
          background === 'white' && styles.whiteBackground,
          areSameFlatColor(getSectionBgColor(props), previousSectionBgColor) && styles.noPaddingTop,
        )}
      >
        <div className={styles.container}>
          <h2 className={styles.title}>{title}</h2>

          {withFilters && (videosWithCrops || videosWithProducts) && hasRunOnLoadEffect && (
            <div className={styles.filtersContainer}>
              <div className={clsx(filterStyles.box, styles.filterBox)}>
                <div className={styles.labelAndFiltersContainer}>
                  <span className={clsx(filterStyles.blueBordersLabel, styles.blueBordersLabel)}>
                    {t('video_section.filters.title', 'Filters')}
                  </span>
                  <ThemeProvider theme={background === 'white' ? darkTheme : lightTheme}>
                    <div className={styles.filterContainer}>
                      <Autocomplete
                        className={clsx(
                          filterStyles.input +
                            (activeFilters.product ? ' ' + filterStyles.inputSelected : ''),
                          styles.filter,
                        )}
                        classes={{
                          listbox: filterStyles.selectListbox,
                          groupUl: filterStyles.selectGroupUl,
                          option: filterStyles.selectOption,
                        }}
                        disableClearable={!activeFilters.product}
                        disabled={productsIdsForFilter.length === 0}
                        options={sortBy(
                          productsIdsForFilter.map(productId => productsById[productId] as Product),
                          'name',
                        ).map(product => product._id)}
                        getOptionLabel={productId => {
                          if (!productId) {
                            return '';
                          }
                          return (productsById[productId] as Product).name;
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('video_section.filters.product', 'Product')}
                          />
                        )}
                        value={activeFilters.product || undefined}
                        onChange={(_, value) => updateFilters('product', value || null)}
                      />

                      <Autocomplete
                        className={clsx(
                          filterStyles.input +
                            (activeFilters.product ? ' ' + filterStyles.inputSelected : ''),
                          styles.filter,
                        )}
                        classes={{
                          listbox: filterStyles.selectListbox,
                          groupUl: filterStyles.selectGroupUl,
                          option: filterStyles.selectOption,
                        }}
                        disableClearable={!activeFilters.crop}
                        disabled={cropsIdsForFilter.length === 0}
                        options={sortBy(
                          cropsIdsForFilter.map(cropId => cropsById[cropId] as Crop),
                          'name',
                        ).map(crop => crop._id)}
                        getOptionLabel={cropId => {
                          if (!cropId) {
                            return '';
                          }
                          return (cropsById[cropId] as Crop).name;
                        }}
                        renderInput={params => (
                          <TextField {...params} label={t('video_section.filters.crop', 'Crop')} />
                        )}
                        value={activeFilters.crop || undefined}
                        onChange={(_, value) => updateFilters('crop', value || null)}
                      />
                    </div>
                  </ThemeProvider>
                </div>
              </div>
            </div>
          )}

          <div className={styles.videos}>
            {currentPageVideos.map((video, i: number) => (
              <div key={i} className={styles.videoWrapper}>
                <Video
                  light
                  title={video.title}
                  url={video.url}
                  date={video.date}
                  maxWidth={maxVideoWidth}
                ></Video>
              </div>
            ))}
          </div>
          <div className={styles.paginationContainer}>{renderPagination()}</div>
        </div>
      </section>
    </BackgroundImage>
  );
}

export default VideosSection;
